import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getFavouriteCount } from '@/hooks/useFavourites';
import FavouritesDrawer from '@/pages/home/components/FavouritesDrawer';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import CustomerLoginModal from '@/components/feature/CustomerLoginModal';
import { useDarkMode } from '@/hooks/useDarkMode';
import { useTheme } from '@/hooks/useTheme';

interface NavbarProps {
  scrolled: boolean;
}

export default function Navbar({ scrolled }: NavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [favDrawerOpen, setFavDrawerOpen] = useState(false);
  const [favCount, setFavCount] = useState(() => getFavouriteCount());
  const [activeSection, setActiveSection] = useState('home');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { user, isLoggedIn, logout } = useCustomerAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const { isDark, toggleTheme } = useDarkMode();
  const { colors, navbarStyle } = useTheme();
  const [loginShake, setLoginShake] = useState(false);

  // Periodically shake login button if not logged in
  useEffect(() => {
    if (isLoggedIn) return;
    const triggerShake = () => {
      setLoginShake(true);
      setTimeout(() => setLoginShake(false), 800);
    };
    // First shake after 3 seconds
    const initial = setTimeout(triggerShake, 3000);
    // Then every 6 seconds
    const interval = setInterval(triggerShake, 6000);
    return () => { clearTimeout(initial); clearInterval(interval); };
  }, [isLoggedIn]);

  useEffect(() => {
    const sync = () => setFavCount(getFavouriteCount());
    window.addEventListener('focus', sync);
    return () => window.removeEventListener('focus', sync);
  }, []);

  useEffect(() => {
    const sectionIds = ['home', 'about', 'services', 'portfolio', 'contact'];
    const handleScroll = () => {
      const scrollY = window.scrollY + 100;
      let current = 'home';
      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) current = id;
      }
      setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerClose = () => {
    setFavDrawerOpen(false);
    setFavCount(getFavouriteCount());
  };

  const navLinks = [
    { name: 'Home', href: '#home', icon: 'ri-home-5-line' },
    { name: 'About', href: '#about', icon: 'ri-information-line' },
    { name: 'Services', href: '#services', icon: 'ri-service-line' },
    { name: 'Collection', href: '#portfolio', icon: 'ri-store-2-line' },
    { name: 'Contact', href: '#contact', icon: 'ri-phone-line' },
  ];

  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(href.replace('#', ''));
    }
    setMobileMenuOpen(false);
  };

  // Dynamic navbar background based on theme and style
  const getNavbarClasses = () => {
    const baseClasses = 'fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b';
    
    if (scrolled) {
      // Scrolled state
      switch (navbarStyle) {
        case 'dark':
          return `${baseClasses} bg-gray-900/98 backdrop-blur-xl border-gray-800`;
        case 'glass':
          return `${baseClasses} bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-white/20 dark:border-slate-700/50`;
        case 'solid':
          return `${baseClasses} bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-800`;
        case 'transparent-scroll':
        default:
          return `${baseClasses} bg-white/98 dark:bg-slate-900/98 backdrop-blur-xl border-gray-100 dark:border-slate-800`;
      }
    } else {
      // Top of page
      switch (navbarStyle) {
        case 'dark':
          return `${baseClasses} bg-gray-900/95 border-gray-800`;
        case 'glass':
          return `${baseClasses} bg-white/60 dark:bg-slate-900/60 backdrop-blur-md border-white/10 dark:border-slate-700/30`;
        case 'solid':
          return `${baseClasses} bg-white dark:bg-slate-900 border-gray-100 dark:border-slate-800`;
        case 'transparent-scroll':
        default:
          return `${baseClasses} bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-gray-100 dark:border-slate-800`;
      }
    }
  };

  // Dynamic logo gradient based on theme
  const getLogoGradient = () => {
    return `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`;
  };

  // Dynamic active nav color
  const getActiveNavClasses = () => {
    return `text-[${colors.primary}] bg-[${colors.primary}15]`;
  };

  return (
    <>
      <nav className={getNavbarClasses()}>
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="flex items-center justify-between h-16 md:h-[68px]">

            {/* Logo */}
            <button
              onClick={() => {
                if (isHomePage) {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  navigate('/');
                }
              }}
              className="flex items-center gap-2.5 cursor-pointer group"
            >
              <div className="relative w-10 h-10 rounded-xl overflow-hidden">
                <div 
                  className="absolute inset-0"
                  style={{ background: getLogoGradient() }}
                ></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <i className="ri-t-shirt-line text-white text-xl"></i>
                </div>
              </div>
              <div className="flex flex-col leading-none">
                <span className="text-[17px] font-black tracking-tight">
                  <span 
                    className="text-transparent bg-clip-text"
                    style={{ backgroundImage: getLogoGradient() }}
                  >Clothy</span>
                  <span className="text-gray-900 dark:text-white">Bazar</span>
                </span>
                <span 
                  className="text-[10px] font-semibold tracking-wide flex items-center gap-1"
                  style={{ color: colors.primary }}
                >
                  <i className="ri-flashlight-fill text-[9px]"></i>30 Min Delivery
                </span>
              </div>
            </button>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const sectionId = link.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'text-white'
                        : scrolled 
                          ? 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-slate-800'
                          : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50/50 dark:hover:bg-slate-800/50'
                    }`}
                    style={isActive ? { backgroundColor: colors.primary } : {}}
                  >
                    {link.name}
                    {isActive && (
                      <span 
                        className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white"
                      ></span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Location pill - desktop */}
              <div 
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full border"
                style={{ 
                  backgroundColor: `${colors.primary}15`,
                  borderColor: `${colors.primary}30`,
                  color: colors.primary 
                }}
              >
                <i className="ri-map-pin-fill text-xs"></i>
                <span className="text-xs font-semibold">Dehradun</span>
              </div>

              {/* Dark Mode Toggle */}
              <button
                onClick={toggleTheme}
                className="relative w-9 h-9 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-slate-600 transition-all cursor-pointer"
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
              >
                <i className={`text-base ${isDark ? 'ri-sun-line text-yellow-400' : 'ri-moon-line text-gray-600'}`}></i>
              </button>

              {/* Favourites */}
              <button
                onClick={() => { setFavCount(getFavouriteCount()); setFavDrawerOpen(true); }}
                className="relative w-9 h-9 rounded-xl bg-gray-100 dark:bg-slate-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-slate-600 transition-all cursor-pointer"
              >
                <i className={`text-base ${favCount > 0 ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-600'}`}></i>
                {favCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-[9px] text-white flex items-center justify-center font-bold">
                    {favCount > 9 ? '9+' : favCount}
                  </span>
                )}
              </button>

              {/* Login / User Avatar */}
              {isLoggedIn && user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(p => !p)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border transition-all cursor-pointer"
                    style={{ backgroundColor: `${colors.primary}15`, borderColor: `${colors.primary}30` }}
                  >
                    <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-white text-xs font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <span className="text-xs font-semibold hidden sm:block max-w-[80px] truncate" style={{ color: colors.primary }}>{user.name.split(' ')[0]}</span>
                    <i className="ri-arrow-down-s-line text-xs" style={{ color: colors.primary }}></i>
                  </button>

                  {/* User dropdown menu */}
                  {showUserMenu && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)}></div>
                      <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50 animate-slide-in">
                        {/* User info */}
                        <div className="px-4 py-3 border-b" style={{ backgroundColor: `${colors.primary}15`, borderColor: `${colors.primary}20` }}>
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-sm">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-gray-900 font-bold text-sm truncate">{user.name}</p>
                              <p className="text-gray-500 text-xs truncate">{user.phone || user.email}</p>
                            </div>
                          </div>
                        </div>
                        {/* Menu Items */}
                        <div className="py-1">
                          <Link
                            to="/profile"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <i className="ri-user-line text-gray-400 w-4 text-center"></i>
                            My Profile
                          </Link>
                          <Link
                            to="/profile?tab=orders"
                            onClick={() => setShowUserMenu(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <i className="ri-shopping-bag-3-line text-gray-400 w-4 text-center"></i>
                            My Orders
                          </Link>
                          <div className="h-px bg-gray-100 mx-4 my-1"></div>
                          <button
                            onClick={() => { logout(); setShowUserMenu(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-logout-box-r-line w-4 text-center"></i>
                            Logout
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <button
                  onClick={() => setShowLoginModal(true)}
                  className={`relative flex flex-col items-center justify-center gap-0 px-3 py-1 rounded-xl text-white transition-all cursor-pointer whitespace-nowrap gradient-primary ${loginShake ? 'animate-login-shake' : ''}`}
                >
                  {/* Ping dot */}
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5">
                    <span className="animate-login-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400 border border-white"></span>
                  </span>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-user-line text-sm"></i>
                    <span className="text-sm font-bold hidden sm:block">Login</span>
                  </div>
                  <span className="hidden sm:block text-[9px] font-medium text-yellow-200 leading-none -mt-0.5 tracking-wide">
                    Tap to sign in
                  </span>
                </button>
              )}

              {/* Profile - desktop (only when logged in) */}
              {isLoggedIn && (
                <Link
                  to="/profile"
                  className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-all"
                >
                  <i className="ri-user-line text-sm"></i>
                  <span>Profile</span>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <button
                className="md:hidden w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center cursor-pointer"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <i className={`text-white text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'}`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => {
                const sectionId = link.href.replace('#', '');
                const isActive = activeSection === sectionId;
                return (
                  <button
                    key={link.name}
                    onClick={() => scrollToSection(link.href)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer ${
                      isActive
                        ? 'gradient-primary text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${link.icon} text-base ${isActive ? 'text-white' : 'text-gray-400'}`}></i>
                    {link.name}
                    {isActive && <i className="ri-check-line ml-auto text-white"></i>}
                  </button>
                );
              })}
              <div className="h-px bg-gray-100 my-2"></div>

              {/* Mobile: Login / User info */}
              {isLoggedIn && user ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ backgroundColor: `${colors.primary}15` }}>
                    <div className="w-9 h-9 rounded-full gradient-primary flex items-center justify-center text-white font-bold">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-gray-900 font-semibold text-sm">{user.name}</p>
                      <p className="text-gray-500 text-xs">{user.phone || user.email}</p>
                    </div>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <i className="ri-user-line text-base text-gray-400"></i>
                    My Profile
                    <i className="ri-arrow-right-s-line ml-auto text-gray-400"></i>
                  </Link>
                  <Link
                    to="/profile?tab=orders"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <i className="ri-shopping-bag-3-line text-base" style={{ color: colors.primary }}></i>
                    My Orders
                    <i className="ri-arrow-right-s-line ml-auto text-gray-400"></i>
                  </Link>
                  <button
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50 cursor-pointer"
                  >
                    <i className="ri-logout-box-r-line text-base"></i>
                    Logout
                  </button>
                </>
              ) : (
                <button
                  onClick={() => { setMobileMenuOpen(false); setShowLoginModal(true); }}
                  className={`relative w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold text-white cursor-pointer gradient-primary ${loginShake ? 'animate-login-shake' : ''}`}
                >
                  {/* Ping dot */}
                  <span className="absolute top-2 right-3 w-2.5 h-2.5">
                    <span className="animate-login-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400 border border-white"></span>
                  </span>
                  <i className="ri-user-line text-base"></i>
                  Login / Register
                  <i className="ri-arrow-right-s-line ml-auto"></i>
                </button>
              )}

              <button
                onClick={() => { setMobileMenuOpen(false); setFavCount(getFavouriteCount()); setFavDrawerOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-700 hover:bg-red-50 cursor-pointer"
              >
                <i className={`text-base ${favCount > 0 ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-400'}`}></i>
                My Favourites
                {favCount > 0 && (
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold">{favCount}</span>
                )}
              </button>
            </div>
          </div>
        )}
      </nav>

      <FavouritesDrawer isOpen={favDrawerOpen} onClose={handleDrawerClose} />

      {/* Login Modal from Navbar */}
      <CustomerLoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onSuccess={() => setShowLoginModal(false)}
        title="Welcome to ClothyBazar"
        subtitle="Login or register with phone or email"
      />
    </>
  );
}
